import { type Dtos, type SuretyAccountId, type UserId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import {
	type DataNeeded,
	type Decision,
	groupDataNeededByProCon,
	groupRelationshipsByProCon,
	type ProCon,
	type ProConRelationship,
	visibleRelationships,
} from "@portal/api/decisioning";
import {
	useFetchAdminDataNeeded,
	useFetchAdminDecisions,
	useFetchAdminProCons,
	useFetchProConRelationships,
	useFetchResolvedDataNeeded,
	useFetchRetiredProCons,
} from "@portal/api/decisioning/admin";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { formatName } from "../../../../utils/string";
import { Spinner } from "../../../primitives";
import { ReportedRetryableLoadError } from "../../load-errors";

export type ListLoadError = { retry: () => void };

type DecisioningData = {
	proCons: readonly ProCon[];
	proConsById: ReadonlyMap<ProCon["id"], ProCon>;
	relationships: readonly ProConRelationship[];
	relationshipsByProCon: ReadonlyMap<ProCon["id"], ProConRelationship[]>;
	dataNeeded: readonly DataNeeded[];
	dataNeededByProCon: ReadonlyMap<ProCon["id"], DataNeeded[]>;
	decisions: readonly Decision[];
	/** False until decisions arrive, so counts don't read 0 while loading or after a failure. */
	decisionsLoaded: boolean;
	retiredProCons: readonly ProCon[];
	resolvedDataNeeded: readonly DataNeeded[];
	/** Set when the list failed to load and nothing is cached, so tabs show a retry instead of an empty state. */
	decisionsLoadError: Option<ListLoadError>;
	retiredLoadError: Option<ListLoadError>;
	resolvedLoadError: Option<ListLoadError>;
	/** Display name for an admin user id; "Underwriter" when unknown. */
	userName: (id: Option<UserId>) => string;
};

const EMPTY_ADMIN_USERS: Dtos.Admin.AdminUser.Get.Response[] = [];
const EMPTY_PRO_CONS: ProCon[] = [];
const EMPTY_DATA_NEEDED: DataNeeded[] = [];
const EMPTY_DECISIONS: Decision[] = [];

const listLoadError = <T,>(
	data: T | undefined,
	error: Error | null,
	refetch: () => Promise<unknown>,
): Option<ListLoadError> =>
	Option.isNone(data) && Option.isSome(error) ? { retry: () => void refetch() } : undefined;

const DecisioningDataContext = createContext<DecisioningData | null>(null);

export const useDecisioningData = () => {
	const context = useContext(DecisioningDataContext);
	if (!context) throw new Error("useDecisioningData must be used inside DecisioningDataProvider");
	return context;
};

/**
 * Loads every list the panel shows. Active lists gate rendering; history lists (decisions,
 * retired, resolved) render empty until they arrive, or expose a load error with a retry.
 */
export const DecisioningDataProvider = ({
	accountId,
	children,
}: {
	accountId: SuretyAccountId;
	children: ReactNode;
}) => {
	const { proCons, proConsIsLoading, proConsError, refetchProCons } =
		useFetchAdminProCons(accountId);
	const { dataNeeded, dataNeededIsLoading, dataNeededError, refetchDataNeeded } =
		useFetchAdminDataNeeded(accountId);
	const { relationships, relationshipsIsLoading, relationshipsError, refetchRelationships } =
		useFetchProConRelationships(accountId);
	const {
		decisions: fetchedDecisions,
		decisionsError,
		refetchDecisions,
	} = useFetchAdminDecisions(accountId);
	const {
		retiredProCons: fetchedRetired,
		retiredProConsError,
		refetchRetiredProCons,
	} = useFetchRetiredProCons(accountId);
	const {
		resolvedDataNeeded: fetchedResolved,
		resolvedDataNeededError,
		refetchResolvedDataNeeded,
	} = useFetchResolvedDataNeeded(accountId);
	const decisions = fetchedDecisions ?? EMPTY_DECISIONS;
	const retiredProCons = fetchedRetired ?? EMPTY_PRO_CONS;
	const resolvedDataNeeded = fetchedResolved ?? EMPTY_DATA_NEEDED;
	const { adminUsers = EMPTY_ADMIN_USERS } = useAdminFetchAdminUsers();

	const userName = useCallback(
		(id: Option<UserId>) =>
			formatName(adminUsers.find((admin) => admin.id === id)) ?? "Underwriter",
		[adminUsers],
	);

	const value = useMemo<DecisioningData | null>(() => {
		if (!proCons || !dataNeeded || !relationships) return null;
		return {
			proCons,
			proConsById: new Map(proCons.map((item) => [item.id, item])),
			relationships,
			relationshipsByProCon: groupRelationshipsByProCon(
				visibleRelationships(relationships, proCons),
			),
			dataNeeded,
			dataNeededByProCon: groupDataNeededByProCon(dataNeeded),
			decisions,
			decisionsLoaded: Option.isSome(fetchedDecisions),
			retiredProCons,
			resolvedDataNeeded,
			decisionsLoadError: listLoadError(fetchedDecisions, decisionsError, refetchDecisions),
			retiredLoadError: listLoadError(fetchedRetired, retiredProConsError, refetchRetiredProCons),
			resolvedLoadError: listLoadError(
				fetchedResolved,
				resolvedDataNeededError,
				refetchResolvedDataNeeded,
			),
			userName,
		};
	}, [
		proCons,
		dataNeeded,
		relationships,
		decisions,
		retiredProCons,
		resolvedDataNeeded,
		fetchedDecisions,
		fetchedRetired,
		fetchedResolved,
		decisionsError,
		retiredProConsError,
		resolvedDataNeededError,
		refetchDecisions,
		refetchRetiredProCons,
		refetchResolvedDataNeeded,
		userName,
	]);

	const loadError = [
		{ error: proConsError, resource: "pros and cons", retry: refetchProCons },
		{ error: dataNeededError, resource: "data needed", retry: refetchDataNeeded },
		{ error: relationshipsError, resource: "factor connections", retry: refetchRelationships },
	].find((entry) => entry.error);

	if (loadError?.error) {
		return (
			<ReportedRetryableLoadError
				error={loadError.error}
				resource={loadError.resource}
				onRetry={() => void loadError.retry()}
				className="m-4"
			/>
		);
	}
	if (proConsIsLoading || dataNeededIsLoading || relationshipsIsLoading || !value) {
		return (
			<div className="flex justify-center p-5">
				<Spinner />
			</div>
		);
	}

	return (
		<DecisioningDataContext.Provider value={value}>{children}</DecisioningDataContext.Provider>
	);
};
